import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import clsx from 'clsx';
import { Text, textVariantClasses } from './Text';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { Spinner } from '../Spinner';

export interface SelectOption {
  value: string;
  content: ReactNode;
}

interface SelectProps {
  label?: string;
  placeholder?: string;
  itemsLabel?: string;
  options: SelectOption[];
  disabled?: boolean;
  loading?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  error?: boolean | string;
  width?: number;
  className?: string;
}

export function Select({
  label,
  placeholder,
  itemsLabel,
  options,
  disabled,
  loading,
  value,
  onValueChange,
  error,
  width,
  className,
}: SelectProps) {
  const { t } = useTranslation();

  return (
    <SelectPrimitive.Root
      disabled={disabled || loading}
      value={value}
      onValueChange={onValueChange}
    >
      <div className={clsx('w-full', className)} style={{ width }}>
        {label && (
          <Text as="label" variant="label" strong className={'mb-2xs block'}>
            {label}
          </Text>
        )}
        <SelectPrimitive.Trigger
          className={clsx(
            'border-transparent-dark-16 data-[placeholder]:text-neutral-muted focus-visible:border-neutral-default data-[state=open]:border-neutral-default outline-neutral-subtle gap-2xs bg-base-white pl-xs pr-2xs py-2xs flex h-[38px] w-full items-center justify-between rounded-md border whitespace-nowrap shadow-xs transition-all focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:outline-2 data-[state=open]:outline-offset-2',
            error && '!border-error-default data-[placeholder]:!text-error-default',
            textVariantClasses['label-sm-default']
          )}
          style={{ width }}
        >
          <SelectPrimitive.Value placeholder={placeholder || t('common.select')} asChild>
            <Text as="span" variant="label" className="truncate">
              {options.find((option) => option.value === value)?.content}
            </Text>
          </SelectPrimitive.Value>
          <SelectPrimitive.Icon asChild>
            {loading ? (
              <Spinner color="black" />
            ) : (
              <ChevronDownIcon size={16} className="text-neutral-muted shrink-0" />
            )}
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        {typeof error === 'string' && error.length > 0 && (
          <Text as="span" variant="label" size="xs" className="text-error-default">
            {error}
          </Text>
        )}
      </div>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="bg-base-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 border-transparent-dark-16 relative z-[100] max-h-(--radix-select-content-available-height) origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-xs data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1"
          position="popper"
        >
          <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
            <ChevronUpIcon size={16} className="text-neutral-muted" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1 p-1">
            <SelectPrimitive.Group>
              {itemsLabel && (
                <SelectPrimitive.Label
                  data-slot="select-label"
                  className={clsx(
                    'py-4xs text-neutral-muted px-2',
                    textVariantClasses['label-xs-strong']
                  )}
                >
                  {itemsLabel}
                </SelectPrimitive.Label>
              )}
              {options.length === 0 ? (
                <Text size="xs" className="px-2xs py-xs">
                  {t('common.selectEmpty')}
                </Text>
              ) : (
                options.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    className={clsx(
                      'focus:bg-neutral-subtle flex w-full cursor-default items-center rounded-sm px-2 py-1.5 outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                      textVariantClasses['label-sm-default']
                    )}
                    value={option.value}
                  >
                    <SelectPrimitive.ItemText>{option.content}</SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                ))
              )}
            </SelectPrimitive.Group>
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
            <ChevronDownIcon size={16} className="text-neutral-muted" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
